import React, { useState } from "react";
import axios from "axios";
import { X, Upload, Download, FileSpreadsheet, Loader2, CheckCircle2, AlertCircle, Info } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

export const ImportPelangganModal = ({ onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [autoProvision, setAutoProvision] = useState(true);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const handleDownloadTemplate = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/pelanggan/import/template`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "template_import_pelanggan.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError("Gagal download template");
    } finally {
      setDownloading(false);
    }
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".xlsx")) {
      setError("File harus Excel (.xlsx). Format lain tidak didukung.");
      setFile(null);
      return;
    }
    setError("");
    setFile(f);
  };

  const handleUpload = async () => {
    if (!file) { setError("Pilih file Excel dulu"); return; }
    setLoading(true); setError(""); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(
        `${BACKEND_URL}/api/pelanggan/import/excel?auto_provision=${autoProvision}`,
        fd,
        { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } }
      );
      setResult(res.data);
      if (res.data.success > 0) {
        setTimeout(() => onSuccess?.(res.data), 500);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Gagal import file");
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="import-pelanggan-modal">
      <div className="w-full max-w-xl rounded-2xl bg-[#0e1628] border border-slate-800 shadow-2xl max-h-[92vh] flex flex-col">
        <div className="p-4 border-b border-slate-800 flex justify-between items-center bg-[#0a1120]">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-emerald-500/20 text-emerald-400 flex items-center justify-center">
              <FileSpreadsheet className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-white">Import Data Pelanggan</h2>
              <p className="text-[11px] text-slate-400">Upload banyak pelanggan sekaligus dari Excel</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white p-1.5" data-testid="close-import-modal"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto">
          {/* Download template */}
          <div className="p-3 rounded-xl bg-cyan-500/5 border border-cyan-500/30">
            <div className="flex items-start gap-3">
              <Download className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-xs font-bold text-cyan-200">Belum punya template?</p>
                <p className="text-[11px] text-slate-400 mt-0.5">Download contoh Excel berisi kolom-kolom yang harus diisi + daftar Wilayah, Paket, ODP, dan Mitra yang tersedia sebagai referensi.</p>
                <button
                  onClick={handleDownloadTemplate}
                  disabled={downloading}
                  className="mt-2 px-3 py-1.5 rounded-lg bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white text-[11px] font-bold flex items-center gap-1.5 disabled:opacity-50"
                  data-testid="download-template-btn"
                >
                  {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                  Download Template Excel (.xlsx)
                </button>
              </div>
            </div>
          </div>

          {/* Format info */}
          <div className="p-3 rounded-xl bg-slate-900/60 border border-slate-800 text-[11px] text-slate-400 flex items-start gap-2">
            <Info className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-amber-300 font-bold mb-1">Format File</p>
              <ul className="list-disc list-inside space-y-0.5">
                <li>Hanya <strong className="text-white">.xlsx</strong> (Microsoft Excel 2007+). Format .xls / .csv / .ods tidak didukung.</li>
                <li>Isi data di sheet <code className="text-emerald-400">Pelanggan</code>, jangan hapus baris header</li>
                <li>Kolom bertanda <span className="text-rose-400">*</span> wajib diisi</li>
                <li>Kode Wilayah / Nama Paket / Kode ODP / Nama Mitra harus <strong className="text-white">persis</strong> sama dengan sheet lookup</li>
                <li><strong className="text-white">Mitra &amp; bagi hasil</strong>: isi kolom "Mitra &amp; % Bagi Hasil". 1 mitra → <code className="text-emerald-400">PT Mitra Nusantara:20</code>; lebih dari 1 mitra pisahkan titik-koma → <code className="text-emerald-400">PT Mitra Nusantara:20; H. Sulaiman:10</code>. Kosong = 100% pusat. Data mitra langsung masuk tanpa perlu edit ulang.</li>
              </ul>
            </div>
          </div>

          {/* Upload */}
          <div>
            <label className="text-xs font-semibold text-slate-300 block mb-2">Pilih File Excel</label>
            <div className="relative">
              <input
                type="file"
                accept=".xlsx"
                onChange={handleFileChange}
                className="w-full text-xs text-slate-400 file:mr-3 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-bold file:bg-slate-800 file:text-emerald-300 hover:file:bg-slate-700 cursor-pointer"
                data-testid="import-file-input"
              />
            </div>
            {file && (
              <div className="mt-2 p-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center gap-2 text-[11px] text-emerald-300" data-testid="selected-file-info">
                <FileSpreadsheet className="w-3.5 h-3.5" />
                <span className="font-mono">{file.name}</span>
                <span className="text-emerald-400/60">· {(file.size / 1024).toFixed(1)} KB</span>
              </div>
            )}
          </div>

          {/* Auto-provision */}
          <label className="flex items-center gap-2 p-2.5 rounded-xl bg-emerald-500/10 border border-emerald-500/30 cursor-pointer">
            <input
              type="checkbox"
              checked={autoProvision}
              onChange={(e) => setAutoProvision(e.target.checked)}
              className="w-4 h-4 accent-emerald-500"
              data-testid="import-auto-provision-toggle"
            />
            <div>
              <p className="text-xs font-bold text-emerald-200">Auto-provision ke Router MikroTik</p>
              <p className="text-[10px] text-emerald-400/80">Otomatis create secret PPPoE untuk baris yang punya kolom "Router (IP:Port)"</p>
            </div>
          </label>

          {/* Error */}
          {error && (
            <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/40 text-rose-300 text-xs flex items-start gap-2" data-testid="import-error">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /><span>{error}</span>
            </div>
          )}

          {/* Result */}
          {result && (
            <div className="p-3 rounded-xl bg-slate-900/60 border border-slate-700 text-xs space-y-2" data-testid="import-result">
              <p className="font-bold text-white flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                {result.message}
              </p>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="p-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                  <p className="text-lg font-mono font-bold text-emerald-300">{result.success}</p>
                  <p className="text-[10px] text-emerald-400">Berhasil</p>
                </div>
                <div className="p-2 rounded-lg bg-cyan-500/10 border border-cyan-500/30">
                  <p className="text-lg font-mono font-bold text-cyan-300">{result.provisioned}</p>
                  <p className="text-[10px] text-cyan-400">Provisioned</p>
                </div>
                <div className="p-2 rounded-lg bg-rose-500/10 border border-rose-500/30">
                  <p className="text-lg font-mono font-bold text-rose-300">{result.failed}</p>
                  <p className="text-[10px] text-rose-400">Gagal</p>
                </div>
              </div>
              {result.errors && result.errors.length > 0 && (
                <div className="mt-2 max-h-32 overflow-y-auto space-y-0.5">
                  <p className="text-[10px] text-rose-300 font-bold">Detail Error:</p>
                  {result.errors.slice(0, 20).map((e, i) => (
                    <p key={i} className="text-[10px] text-rose-300/80 font-mono">{e}</p>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 p-4 border-t border-slate-800 bg-[#0a1120]">
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-xs font-bold text-slate-300 bg-slate-800 hover:bg-slate-700" data-testid="cancel-import-btn">
            {result ? "Selesai" : "Batal"}
          </button>
          {!result && (
            <button
              onClick={handleUpload}
              disabled={loading || !file}
              className="px-4 py-2 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 flex items-center gap-2 disabled:opacity-50 shadow-lg shadow-emerald-600/30"
              data-testid="upload-excel-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {loading ? "Mengimpor..." : "Upload & Proses"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
